import React, { useState, useEffect } from 'react';
import { AlertCircle, Info, CheckCircle, XCircle, X, ExternalLink } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const Announcements = ({ location = 'all' }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [dismissed, setDismissed] = useState([]);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchAnnouncements();
    // Load dismissed announcements from localStorage
    const stored = localStorage.getItem('dismissed_announcements');
    if (stored) {
      setDismissed(JSON.parse(stored));
    }
  }, [location]);

  const fetchAnnouncements = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/announcements/?location=${location}`);
      const data = await response.json();
      setAnnouncements(data);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    }
  };

  const handleDismiss = (id) => {
    const newDismissed = [...dismissed, id];
    setDismissed(newDismissed);
    localStorage.setItem('dismissed_announcements', JSON.stringify(newDismissed));
  };

  const getTypeConfig = (type) => {
    switch (type) {
      case 'info':
        return {
          icon: <Info className="w-5 h-5" />,
          bgColor: 'bg-blue-50 dark:bg-blue-900/20',
          borderColor: 'border-blue-200 dark:border-blue-800',
          textColor: 'text-blue-900 dark:text-blue-100',
          iconColor: 'text-blue-600 dark:text-blue-400',
        };
      case 'success':
        return {
          icon: <CheckCircle className="w-5 h-5" />,
          bgColor: 'bg-green-50 dark:bg-green-900/20',
          borderColor: 'border-green-200 dark:border-green-800',
          textColor: 'text-green-900 dark:text-green-100',
          iconColor: 'text-green-600 dark:text-green-400',
        };
      case 'warning':
        return {
          icon: <AlertCircle className="w-5 h-5" />,
          bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
          borderColor: 'border-yellow-200 dark:border-yellow-800',
          textColor: 'text-yellow-900 dark:text-yellow-100',
          iconColor: 'text-yellow-600 dark:text-yellow-400',
        };
      case 'danger':
        return {
          icon: <XCircle className="w-5 h-5" />,
          bgColor: 'bg-red-50 dark:bg-red-900/20',
          borderColor: 'border-red-200 dark:border-red-800',
          textColor: 'text-red-900 dark:text-red-100',
          iconColor: 'text-red-600 dark:text-red-400',
        };
      default:
        return {
          icon: <Info className="w-5 h-5" />,
          bgColor: 'bg-gray-50 dark:bg-gray-900/20',
          borderColor: 'border-gray-200 dark:border-gray-800',
          textColor: 'text-gray-900 dark:text-gray-100',
          iconColor: 'text-gray-600 dark:text-gray-400',
        };
    }
  };

  const visibleAnnouncements = announcements.filter(ann => !dismissed.includes(ann.id));

  if (visibleAnnouncements.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {visibleAnnouncements.map((announcement) => {
        const config = getTypeConfig(announcement.type);
        
        return (
          <Card
            key={announcement.id}
            className={`group overflow-hidden hover:shadow-2xl transition-all duration-300 h-full border-2 ${config.borderColor} hover:scale-105`}
          >
            {/* Image Section */}
            {announcement.image && (
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={announcement.image} 
                  alt={announcement.title_ar}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                {/* Type Badge on Image */}
                <div className={`absolute top-3 right-3 ${config.bgColor} ${config.borderColor} border-2 px-4 py-2 rounded-full shadow-lg backdrop-blur-sm`}>
                  <div className="flex items-center gap-2">
                    <div className={config.iconColor}>
                      {config.icon}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Content Section */}
            <CardContent className={`p-6 ${config.bgColor} flex flex-col h-full ${announcement.image ? '' : 'min-h-[280px]'}`}>
              {/* Icon and Title */}
              <div className="flex items-start gap-3 mb-4">
                {!announcement.image && (
                  <div className={`${config.iconColor} flex-shrink-0 mt-1`}>
                    {config.icon}
                  </div>
                )}
                <div className="flex-1">
                  <h3 className={`font-bold text-xl mb-2 ${config.textColor} leading-tight`}>
                    {announcement.title_ar}
                  </h3>
                  {announcement.title_en && (
                    <p className={`text-sm ${config.textColor} opacity-70 mb-2`}>
                      {announcement.title_en}
                    </p>
                  )}
                </div>
                
                {/* Dismiss Button */}
                <button
                  onClick={() => handleDismiss(announcement.id)}
                  className={`${config.iconColor} hover:opacity-70 transition-opacity flex-shrink-0`}
                  aria-label="إغلاق"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Content */}
              <p className={`${config.textColor} opacity-90 leading-relaxed mb-4 flex-1`}>
                {announcement.content_ar}
              </p>
              
              {announcement.content_en && (
                <p className={`${config.textColor} opacity-75 text-sm leading-relaxed mb-4 flex-1 italic`}>
                  {announcement.content_en}
                </p>
              )}
              
              {/* Link Button */}
              {announcement.link && (
                <div className="mt-auto pt-4 border-t border-current/20">
                  <a
                    href={announcement.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-2 font-bold hover:underline ${config.iconColor} hover:gap-3 transition-all`}
                  >
                    {announcement.link_text_ar}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default Announcements;
